// <JC

import React, { useState, useEffect, useRef } from "react";
import { store } from "../../reducer/Store";
import ApiService from "../../services/ApiService";
import Utils from "../../services/Utils";
import { ToastContainer } from "react-toastify";
import { Button, Card, Alert } from "react-bootstrap";
import OperationItem from "./OperationItem"; // Import du composant enfant

const OperationTypesDistinct = () => {
  const { dispatch, state } = React.useContext(store);
  
  // ÉTAT LOCAL : Stocke la liste des opérations dans un format facile à éditer (tags)
  const [editableOperations, setEditableOperations] = React.useState([]);

  // Référence pour éviter l'appel à la sauvegarde lors du chargement initial (mount)
  const isInitialMount = useRef(true); 

  // FONCTION UTILITAIRE : Structure les données reçues de l'API en liste éditable
  const processReceivedData = (data) => {
    let operationsList = [];
    let currentBrand = null;
    let idCounter = 1;

    data.forEach(item => {
      if (item.type === 'brand') {
        currentBrand = item.data;
      } else if (item.type === 'operation_type' && currentBrand) {
        operationsList.push({
          id: idCounter++, // ID local pour React (clé unique)
          brand: currentBrand,
          operation_type: item.data,
          operation_type_id: item.operation_type_id, // ID pour la DB
          // Utilise les tags existants renvoyés par l'API
          tags: item.tags || [], 
        });
      }
    });
    setEditableOperations(operationsList);
  };

  /**
   * Logique pour ajouter un tag à une opération spécifique
   */
  const addTagToOperation = (operationId, newTag) => {
    if (!newTag.trim()) return;

    setEditableOperations(prevOps =>
      prevOps.map(op => {
        if (op.id === operationId) {
          const formattedTag = newTag.trim();
          // Vérifie l'absence de doublons
          if (!op.tags.includes(formattedTag)) {
            return {
              ...op,
              tags: [...op.tags, formattedTag],
            };
          }
        }
        return op;
      })
    );
  };

  /**
   * Logique pour supprimer un tag d'une opération spécifique
   */
  const removeTagFromOperation = (operationId, tagToRemove) => {
    setEditableOperations(prevOps =>
      prevOps.map(op => {
        if (op.id === operationId) {
          return {
            ...op,
            tags: op.tags.filter(tag => tag !== tagToRemove),
          };
        }
        return op;
      })
    );
  };
  
  /**
   * Logique pour enregistrer TOUTES les correspondances (tags) en base de données
   */
  const saveCorrespondances = async () => {
    // Prépare les données pour l'API
    const dataToSave = editableOperations
      .map(op => ({
        operation_type_id: op.operation_type_id, 
        tags: op.tags, // Tableau des tags pour l'enregistrement
      }));

    // Pas de vérification de dataToSave.length, car même un tableau vide 
    // peut signifier la suppression de tous les tags pour une opération.

    dispatch({ type: "SET_LOADER", payload: true });
    ApiService.request(
      { data: dataToSave }, 
      `operation-type-tag`,
      "post" 
    )
      .then(function (response) {
        dispatch({ type: "SET_LOADER", payload: false });
        //Utils.notifySuccess("Correspondances enregistrées");
      })
      .catch(function (error) {
        console.error(error);
        dispatch({ type: "SET_LOADER", payload: false });
        Utils.notifyError("Échec de l'enregistrement automatique des correspondances.");
      });
  };

  /**
   * Logique pour récupérer les données de l'API
   */
  const getOperationTypesDistinct = async () => {
    var brands = [];
    JSON.parse(localStorage.getItem("currentBrands")).map((brand) =>
      brands.push(brand.value)
    );
    if(brands.length === 0){ brands = [0]; }
    
    dispatch({ type: "SET_LOADER", payload: true });
    ApiService.request(
      {},
      `operation-types-distinct-by-brands-year/${brands}/${
        JSON.parse(localStorage.getItem("currentYear")).value
      }`,
      "get"
    )
      .then(function (response) {
        dispatch({ type: "SET_LOADER", payload: false });
        const operationsTypeDistinct = response.data;
        dispatch({ type: "SET_OPERATIONSTYPEDISTINCT", payload: operationsTypeDistinct });
        
        // Stocker les données traitées dans l'état local pour l'édition des tags
        processReceivedData(operationsTypeDistinct); 
      })
      .catch(function (error) {
        console.log(error);
        dispatch({ type: "SET_LOADER", payload: false });
      });
  };

  /**
   * Cette fonction télécharge un fichier Excel pour une marque et une année données.
   */
  const downloadXlsByBrandAndYear = async () => {
    var brands = [];
    JSON.parse(localStorage.getItem("currentBrands")).map((brand) =>
      brands.push(brand.value)
    );
    if(brands.length === 0){ brands = [0]; }
    ApiService.requestBlobFile(
      {},
      `export-operation-types-distinct-by-brands-year/${brands}/${
        JSON.parse(localStorage.getItem("currentYear")).value
      }`,
      "get"
    )
      .then(function (response) {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${
            "Trade_" + state.currentYear.label + "-Labels-operations"
          }.csv`
        );
        document.body.appendChild(link);
        link.click();
        Utils.notifySuccess("Fichier excel téléchargé");
      })
      .catch(function (error) {
        console.log(error);
        Utils.notifyError("du téléchargement du fichier excel");
        if (error.response.data.message === "Unauthenticated.") {
          localStorage.clear();
          dispatch({ type: "SET_LOGOUT", payload: false });
        }
      });
  };
  
  // 1. useEffect pour la récupération des données au chargement et changement de filtres
  useEffect(() => {
    getOperationTypesDistinct();
  }, [state.currentYear, state.currentBrands]);

  // 2. useEffect pour la sauvegarde automatique (Debounce)
  useEffect(() => {
    // Empêche l'exécution de la sauvegarde lors du premier chargement des données
    if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
    }
    
    // Délai de 500ms avant d'appeler la fonction de sauvegarde
    const timeoutId = setTimeout(() => {
        if (editableOperations.length > 0) {
            saveCorrespondances();
        }
    }, 500); 

    // Nettoyage: annule la sauvegarde si l'état change à nouveau avant le délai
    return () => clearTimeout(timeoutId);

// eslint-disable-next-line react-hooks/exhaustive-deps
}, [editableOperations]); 


  return (
    <>
        <h1>Labels des opérations</h1>
        <h2>Et correspondances des labels fournisseurs</h2>
        <br />
        <br />
        
        {state.currentBrands.length > 0 ?
        (
        <div style={{ marginBottom: '20px' }}>
          <Button onClick={downloadXlsByBrandAndYear} className="me-2">
            Télécharger les labels d'opérations
          </Button>
          
          {/* Le bouton d'enregistrement manuel est retiré car la sauvegarde est automatique */}

        </div>
        ) : (
          <>
            <Card className="text-center">
              <Card.Body>
                <Alert variant="danger" className="m-0">
                  Veuillez sélectionner au moins une marque.
                </Alert>
              </Card.Body>
            </Card>
          </>
        )}
        
        {/* Affichage de la liste éditable */}
        <div>
        {
            editableOperations.map((item) => (
                <OperationItem 
                    key={item.id} 
                    operation={item} 
                    onAddTag={addTagToOperation}
                    onRemoveTag={removeTagFromOperation}
                />
            ))
        }
        </div>
        <ToastContainer />

        <div style={{ marginBottom: '20px' }}>
          <Button onClick={downloadXlsByBrandAndYear} className="me-2">
            Télécharger les labels d'opérations
          </Button>
        </div>
    </>
  );
};

export default OperationTypesDistinct;

// JC>